import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { safeUrl } from '../../utils/format';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useApp } from '../../context';

function Modal({ title, onClose, children }) {
  const isDark = useDarkMode();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', borderRadius: 10, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: isDark ? '1px solid rgb(51 65 85)' : 'none' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>✕</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 9, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 };

function ContactForm({ initial, clients, onSave, onClose }) {
  const isDark = useDarkMode();
  const lbl = { fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 };
  const fld = { width: '100%', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, padding: '6px 8px', fontSize: 10, fontFamily: 'Montserrat,sans-serif', outline: 'none', boxSizing: 'border-box' };
  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    job_title: initial?.job_title || '',
    linkedin_url: initial?.linkedin_url || '',
    notes: initial?.notes || '',
    client_id: initial?.client_id || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.client_id) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl}>First Name *</label>
          <input value={form.first_name} onChange={e => set('first_name', e.target.value)} style={fld} />
        </div>
        <div>
          <label style={lbl}>Last Name *</label>
          <input value={form.last_name} onChange={e => set('last_name', e.target.value)} style={fld} />
        </div>
        <div>
          <label style={lbl}>Email</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={fld} />
        </div>
        <div>
          <label style={lbl}>Phone</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} style={fld} />
        </div>
        <div>
          <label style={lbl}>Job Title</label>
          <input value={form.job_title} onChange={e => set('job_title', e.target.value)} style={fld} />
        </div>
        <div>
          <label style={lbl}>Organisation *</label>
          <select value={form.client_id} onChange={e => set('client_id', e.target.value)} style={fld}>
            <option value="">Select…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={lbl}>LinkedIn URL</label>
          <input value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/…" style={fld} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={lbl}>Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
            style={{ ...fld, resize: 'vertical' }} />
        </div>
      </div>
      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose}
          style={{ padding: '6px 16px', background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, fontSize: 10, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', color: isDark ? 'rgb(241 245 249)' : '#111827' }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving || !form.first_name || !form.last_name || !form.client_id}
          title={!form.client_id ? 'A contact belongs to an organisation: pick one' : undefined}
          style={{ padding: '6px 16px', background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 10, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', fontWeight: 600 }}>
          {saving ? 'Saving…' : 'Save Contact'}
        </button>
      </div>
    </div>
  );
}

function ContactDetail({ contact, activities, onEdit, onDelete }) {
  const isDark = useDarkMode();
  const contactActivities = activities.filter(a => a.contact_id === contact.id);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#374151', flexShrink: 0 }}>
          {(contact.first_name[0] || '') + (contact.last_name[0] || '')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{contact.first_name} {contact.last_name}</div>
          {contact.job_title && <div style={{ fontSize: 9, color: isDark ? 'rgb(148 163 184)' : '#6B7280', marginTop: 1 }}>{contact.job_title}</div>}
          {contact.client_name && <div style={{ fontSize: 9, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>{contact.client_name}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onEdit} style={{ fontSize: 8, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Edit</button>
          <button onClick={onDelete} style={{ fontSize: 8, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Delete</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {contact.email && (
          <div>
            <div style={{ fontSize: 7, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>Email</div>
            <a href={`mailto:${contact.email}`} style={{ fontSize: 9, color: '#3B82F6', textDecoration: 'none' }}>{contact.email}</a>
          </div>
        )}
        {contact.phone && (
          <div>
            <div style={{ fontSize: 7, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>Phone</div>
            <a href={`tel:${contact.phone}`} style={{ fontSize: 9, color: '#3B82F6', textDecoration: 'none' }}>{contact.phone}</a>
          </div>
        )}
        {safeUrl(contact.linkedin_url) && (
          <div style={{ gridColumn: '1/-1' }}>
            <div style={{ fontSize: 7, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>LinkedIn</div>
            <a href={safeUrl(contact.linkedin_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, color: '#3B82F6', textDecoration: 'none' }}>
              {contact.linkedin_url.replace('https://', '')}
            </a>
          </div>
        )}
        {contact.last_contact_date && (
          <div>
            <div style={{ fontSize: 7, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>Last Contact</div>
            <div style={{ fontSize: 9, color: isDark ? 'rgb(241 245 249)' : '#374151' }}>{new Date(contact.last_contact_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
          </div>
        )}
      </div>
      {contact.notes && (
        <div style={{ marginBottom: 14, padding: 10, background: isDark ? 'rgb(15 23 42)' : '#F9FAFB', borderRadius: 6 }}>
          <div style={{ fontSize: 7, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 9, color: isDark ? 'rgb(241 245 249)' : '#374151', lineHeight: 1.5 }}>{contact.notes}</div>
        </div>
      )}
      <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#374151', marginBottom: 8 }}>Activity ({contactActivities.length})</div>
      {contactActivities.length === 0 && <div style={{ fontSize: 9, color: '#9CA3AF' }}>No activities logged for this contact</div>}
      {contactActivities.map(a => (
        <div key={a.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}` }}>
          <div style={{ fontSize: 12 }}>{{ call:'📞', meeting:'🤝', email:'✉️', note:'📝', linkedin:'💼' }[a.type] || '📝'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{a.subject}</div>
            {a.body && <div style={{ fontSize: 8, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>{a.body}</div>}
            {a.opportunity_name && <div style={{ fontSize: 7, color: '#9CA3AF', marginTop: 1 }}>{a.opportunity_name}</div>}
          </div>
          <div style={{ fontSize: 7, color: '#9CA3AF', flexShrink: 0 }}>{new Date(a.activity_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
        </div>
      ))}
    </div>
  );
}

export default function CrmContactsPage() {
  const isDark = useDarkMode();
  const { detailId } = useApp();
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');

  const load = async () => {
    const [ct, acts, cl] = await Promise.all([
      api.get('crm/contacts'),
      api.get('crm/activities', { limit: 500 }),
      api.get('clients'),
    ]);
    setContacts(ct || []);
    setActivities(acts || []);
    setClients(cl?.clients || cl || []);
    setLoading(false);
    return ct || [];
  };

  // A link such as #crm-contacts/<id> (the PMO dashboard's contact card) opens on that contact.
  useEffect(() => { load().then(ct => { if (detailId) setSelected(ct.find(c => c.id === detailId) || null); }); }, []);

  const createContact = async (form) => {
    await api.post('crm/contacts', form);
    setShowNew(false);
    await load();
  };

  const updateContact = async (form) => {
    await api.put(`crm/contacts/${editing.id}`, form);
    setEditing(null);
    setSelected(null);
    await load();
  };

  const deleteContact = async () => {
    if (!confirm(`Delete ${selected.first_name} ${selected.last_name}?`)) return;
    await api.delete(`crm/contacts/${selected.id}`);
    setSelected(null);
    await load();
  };

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${c.first_name} ${c.last_name} ${c.email || ''} ${c.job_title || ''} ${c.client_name || ''}`.toLowerCase().includes(q);
    const matchClient = !filterClient || c.client_id === filterClient;
    return matchSearch && matchClient;
  });

  if (loading) return <div style={{ padding: 24, fontSize: 11, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>Loading…</div>;

  return (
    <div style={{ padding: '16px 20px', display: 'flex', gap: 16, height: 'calc(100vh - 80px)' }}>
      {/* Left panel */}
      <div style={{ width: 'min(320px, 38vw)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h1 style={{ fontSize: 14, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827', margin: 0, flex: 1 }}>Contacts</h1>
          <button onClick={() => setShowNew(true)}
            style={{ padding: '5px 12px', background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 9, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', fontWeight: 600 }}>
            + Add
          </button>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts…"
          style={{ width: '100%', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, padding: '6px 8px', fontSize: 10, fontFamily: 'Montserrat,sans-serif', outline: 'none', boxSizing: 'border-box' }} />
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
          style={{ width: '100%', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, padding: '6px 8px', fontSize: 10, fontFamily: 'Montserrat,sans-serif', outline: 'none' }}>
          <option value="">All organisations</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 && <div style={{ fontSize: 9, color: '#9CA3AF', padding: '12px 0' }}>No contacts found</div>}
          {filtered.map(c => {
            const actCount = activities.filter(a => a.contact_id === c.id).length;
            const isSelected = selected?.id === c.id;
            return (
              <div key={c.id} onClick={() => setSelected(c)}
                style={{ padding: '10px 10px', marginBottom: 4, borderRadius: 7, cursor: 'pointer', border: `1px solid ${isSelected ? '#1B2A4A' : isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, background: isSelected ? '#1B2A4A' : isDark ? 'rgb(30 41 59)' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#374151', flexShrink: 0 }}>
                    {(c.first_name[0]||'') + (c.last_name[0]||'')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: 8, color: isDark ? 'rgb(148 163 184)' : '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.job_title || ''}{c.job_title && c.client_name ? ' · ' : ''}{c.client_name || ''}
                    </div>
                  </div>
                  {actCount > 0 && <span style={{ fontSize: 7, color: '#9CA3AF' }}>{actCount}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, minWidth: 0, background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, padding: 16, overflowY: 'auto' }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 11, color: '#9CA3AF' }}>
            Select a contact
          </div>
        ) : (
          <ContactDetail
            contact={selected}
            activities={activities}
            onEdit={() => setEditing(selected)}
            onDelete={deleteContact}
          />
        )}
      </div>

      {showNew && (
        <Modal title="New Contact" onClose={() => setShowNew(false)}>
          <ContactForm clients={clients} onSave={createContact} onClose={() => setShowNew(false)} />
        </Modal>
      )}
      {editing && (
        <Modal title="Edit Contact" onClose={() => setEditing(null)}>
          <ContactForm initial={editing} clients={clients} onSave={updateContact} onClose={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}
